/* Per-kind payload renderers.

   The raw JSON is always one toggle away, so these exist to answer the question
   a person actually has about each kind of step — which chunks were dropped,
   which check failed, what the patch changed, who approved it and how long they
   took. A generic key/value dump can technically show all of that; nobody
   reads it. */

import React, { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { fmtMs, fmtTokens } from "@/shared/lib/runs";
import { chipCls, EmptyNote, ErrorNote, FieldRow, MiniBar, PassPill, TierChip, TruncText } from "./atoms";
import { money } from "./money";

import type { ComponentType, ReactNode } from "react";

import type { TraceChunk, TraceKind } from "@/shared/types";
import type {
  ApprovalInputPayload, ApprovalOutputPayload, BillableSpan, CodeInputPayload,
  CodeOutputPayload, CompactionOutputPayload, EditOutputPayload,
  EffectEntry, GuardrailInputPayload, GuardrailOutputPayload, HandoffInputPayload,
  HandoffOutputPayload, KnowledgeOutputPayload, MemoryOutputPayload, PlanOutputPayload,
  RetryOutputPayload, RouteInputPayload, RouteOutputPayload, SearchOutputPayload, SkillInputPayload, Span, SubagentOutputPayload,
  ThinkingOutputPayload, TokenUsage, VerifierOutputPayload, WebhookOutputPayload,
} from "../types";

/* Collapsed group — used wherever a list has a "the rest" tail that is worth
   keeping but not worth showing. */
function More({ label, children, defaultOpen = false }: {
  label: ReactNode;
  children?: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-1.5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="press inline-flex items-center gap-1 text-[11.5px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
      >
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={2} />
        {label}
      </button>
      {open && <div className="mt-1.5">{children}</div>}
    </div>
  );
}

function Card({ children, tone = "" }: { children?: ReactNode; tone?: string }) {
  return (
    <div className={`rounded-[8px] px-2.5 py-2 ring-1 ${tone || "bg-[var(--surface-sunk)] ring-[var(--hairline)]"}`}>
      {children}
    </div>
  );
}

function Stack({ children }: { children?: ReactNode }) {
  return <div className="space-y-1.5">{children}</div>;
}

/* ------------------------------- retrieval -------------------------------- */

function MemoryOutput({ data }: { data: MemoryOutputPayload }) {
  if (!data.memories?.length) return <EmptyNote>Nothing recalled.</EmptyNote>;
  return (
    <Stack>
      {data.memories.map((m, i) => (
        <Card key={i}>
          <TruncText text={m} limit={200} className="text-[12.5px] leading-[1.6] text-[var(--text-primary)]" />
        </Card>
      ))}
    </Stack>
  );
}

function Chunk({ c, dropped }: { c: TraceChunk; dropped?: boolean }) {
  return (
    <Card>
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono text-[11.5px] font-medium text-[var(--text-primary)] truncate">{c.file}</span>
        {c.folder && <span className="text-[11px] text-[var(--text-quaternary)] truncate">{c.folder}</span>}
        <span className="ml-auto flex items-center gap-1.5 shrink-0">
          <MiniBar value={c.score} tone={dropped ? "warn" : "success"} width={34} />
          <span className="font-mono text-[11px] tabular-nums text-[var(--text-tertiary)]">{c.score?.toFixed(2)}</span>
        </span>
      </div>
      {c.excerpt && (
        <TruncText text={c.excerpt} limit={140} className="block mt-1 text-[11.5px] leading-[1.55] text-[var(--text-secondary)]" />
      )}
    </Card>
  );
}

/* Dropped chunks are shown, not hidden: a wrong answer is very often a
   retrieval that ranked the right passage just below the cutoff, and that is
   invisible if the trace only records what made it in. */
function KnowledgeOutput({ data }: { data: KnowledgeOutputPayload }) {
  const used = data.used || [];
  const dropped = data.dropped || [];
  return (
    <div>
      {used.length === 0 ? (
        <EmptyNote>No chunks passed the threshold.</EmptyNote>
      ) : (
        <Stack>{used.map((c, i) => <Chunk key={i} c={c} />)}</Stack>
      )}
      {dropped.length > 0 && (
        <More label={`${dropped.length} chunk${dropped.length === 1 ? "" : "s"} retrieved but not used`}>
          <Stack>{dropped.map((c, i) => <Chunk key={i} c={c} dropped />)}</Stack>
        </More>
      )}
    </div>
  );
}

function SkillInput({ data }: { data: SkillInputPayload }) {
  const considered = data.considered || [];
  return (
    <div>
      <FieldRow label="Query">
        <TruncText text={data.query} className="text-[12.5px]" />
      </FieldRow>
      {considered.length > 0 && (
        <div className="py-2">
          <div className="text-[11.5px] text-[var(--text-tertiary)] mb-1.5">Candidates · {considered.length}</div>
          <Stack>
            {considered.map((c, i) => (
              <Card key={i} tone={c.picked ? "bg-[var(--success-bg)] ring-[var(--success-ring)]" : ""}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-[11.5px] font-medium text-[var(--text-primary)] truncate">{c.id}</span>
                  {c.org && <span className={chipCls}>{c.org}</span>}
                  {c.picked && <span className="ml-auto shrink-0"><PassPill pass>picked</PassPill></span>}
                </div>
                {c.why && <div className="text-[11.5px] leading-[1.5] text-[var(--text-secondary)] mt-1">{c.why}</div>}
              </Card>
            ))}
          </Stack>
        </div>
      )}
    </div>
  );
}

/* --------------------------------- intent --------------------------------- */

function PlanOutput({ data }: { data: PlanOutputPayload }) {
  const steps = data.steps || [];
  if (!steps.length) return <EmptyNote>Empty plan.</EmptyNote>;
  return (
    <ol className="m-0 p-0 list-none space-y-1">
      {steps.map((s, i) => {
        const text = typeof s === "string" ? s : s.text;
        const done = typeof s === "object" && s.done;
        return (
          <li key={i} className="flex items-start gap-2">
            <span className="shrink-0 mt-[1px] w-[15px] h-[15px] rounded-[4px] bg-[var(--surface-2)] ring-1 ring-[var(--hairline)] flex items-center justify-center font-mono text-[10px] tabular-nums text-[var(--text-tertiary)]">
              {i + 1}
            </span>
            <span className={`text-[12.5px] leading-[1.55] ${done ? "text-[var(--text-tertiary)] line-through" : "text-[var(--text-primary)]"}`}>
              {text}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function ThinkingOutput({ data }: { data: ThinkingOutputPayload }) {
  return (
    <div className="pl-2.5 border-l-2 border-[var(--info-ring)] py-1">
      <TruncText text={data.text} limit={600} className="text-[12.5px] leading-[1.7] text-[var(--text-secondary)] italic" />
    </div>
  );
}

/* ---------------------------------- work ---------------------------------- */

function SearchOutput({ data }: { data: SearchOutputPayload }) {
  const sources = data.sources || [];
  if (!sources.length) return <EmptyNote>No sources returned.</EmptyNote>;
  return (
    <Stack>
      {sources.map((s, i) => (
        <Card key={i}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[12.5px] text-[var(--text-primary)] truncate">{s.title}</span>
            <span className="ml-auto shrink-0 font-mono text-[11px] text-[var(--text-quaternary)]">{s.domain}</span>
            {s.cited && <PassPill pass>cited</PassPill>}
          </div>
        </Card>
      ))}
    </Stack>
  );
}

function CodeInput({ data }: { data: CodeInputPayload }) {
  return (
    <div>
      <FieldRow label="Command">
        <span className="font-mono text-[12.5px] leading-[1.6] break-all text-[var(--text-primary)]">{data.command}</span>
      </FieldRow>
      {data.runtime && <FieldRow label="Runtime"><span className={chipCls}>{data.runtime}</span></FieldRow>}
      {data.files?.length > 0 && (
        <FieldRow label="Files">
          <span className="flex items-center gap-1.5 flex-wrap">
            {data.files.map((f) => <span key={f} className={chipCls}>{f}</span>)}
          </span>
        </FieldRow>
      )}
    </div>
  );
}

/* A non-zero exit with empty stderr is a real and confusing failure mode, so
   the exit code is always rendered — never inferred from the presence of
   output. */
function CodeOutput({ data }: { data: CodeOutputPayload }) {
  const failed = (data.exit_code ?? 0) !== 0;
  return (
    <div>
      <FieldRow label="Exit code">
        <PassPill pass={!failed}>{String(data.exit_code ?? 0)}</PassPill>
      </FieldRow>
      {data.stdout && (
        <FieldRow label="stdout">
          <TruncText mono text={data.stdout} limit={500} className="block text-[12px] leading-[1.65] text-[var(--text-primary)]" />
        </FieldRow>
      )}
      {data.stderr ? (
        <FieldRow label="stderr">
          <TruncText mono text={data.stderr} limit={500} className="block text-[12px] leading-[1.65] text-[var(--danger)]" />
        </FieldRow>
      ) : null}
    </div>
  );
}

/* Unified diff. Colour carries the sign so the eye finds the change before it
   reads the line, but the +/− stays in the text — colour alone is not a
   signal everyone receives. */
function Patch({ patch }: { patch: string }) {
  const lines = String(patch).split("\n");
  return (
    <div className="rounded-[8px] ring-1 ring-[var(--hairline)] overflow-hidden font-mono text-[11.5px] leading-[1.7]">
      {lines.map((ln, i) => {
        const c = ln[0];
        const tone =
          c === "+" ? "bg-[var(--success-bg)] text-[var(--success)]"
          : c === "-" ? "bg-[var(--danger-bg)] text-[var(--danger)]"
          : c === "@" ? "bg-[var(--surface-2)] text-[var(--text-tertiary)]"
          : "text-[var(--text-secondary)]";
        return (
          <div key={i} className={`px-2.5 whitespace-pre-wrap break-words ${tone}`}>
            {ln || " "}
          </div>
        );
      })}
    </div>
  );
}

function EditOutput({ data }: { data: EditOutputPayload }) {
  return (
    <div>
      <FieldRow label="Change">
        <span className="font-mono text-[12.5px] tabular-nums">
          <span className="text-[var(--success)]">+{data.added}</span>{" "}
          <span className="text-[var(--danger)]">−{data.removed}</span>
        </span>
      </FieldRow>
      {data.patch ? <div className="py-2"><Patch patch={data.patch} /></div> : <EmptyNote>No patch recorded.</EmptyNote>}
    </div>
  );
}

/* ------------------------------- delegation -------------------------------- */

function SubagentOutput({ data }: { data: SubagentOutputPayload }) {
  const u: TokenUsage = data.usage || {};
  return (
    <div>
      {data.returned ? (
        <FieldRow label="Returned">
          <TruncText text={data.returned} limit={400} className="text-[12.5px] leading-[1.6]" />
        </FieldRow>
      ) : (
        <EmptyNote>Nothing returned.</EmptyNote>
      )}
      <div className="flex items-center gap-1.5 flex-wrap pt-2">
        <span className={chipCls}>↑ {fmtTokens(u.input_tokens || 0)} in</span>
        <span className={chipCls}>↓ {fmtTokens(u.output_tokens || 0)} out</span>
      </div>
    </div>
  );
}

/* The handoff is the unit this product is built around, so its panel answers
   the three questions in order: what was promised, what was sent, and what the
   verifier said about the gap. */
function HandoffInput({ data }: { data: HandoffInputPayload }) {
  return (
    <div>
      {data.contract && (
        <FieldRow label="Contract">
          <TruncText text={data.contract} className="text-[12.5px] leading-[1.6]" />
        </FieldRow>
      )}
      {data.required?.length > 0 && (
        <FieldRow label="Required fields">
          <span className="flex items-center gap-1.5 flex-wrap">
            {data.required.map((f) => <span key={f} className={chipCls}>{f}</span>)}
          </span>
        </FieldRow>
      )}
      {data.payload && (
        <FieldRow label="Payload">
          <TruncText mono text={data.payload} className="text-[12.5px] leading-[1.6]" />
        </FieldRow>
      )}
    </div>
  );
}

function HandoffOutput({ data }: { data: HandoffOutputPayload }) {
  const passed = data.verdict !== "violated" && data.verdict !== "fail";
  return (
    <div>
      <FieldRow label="Verdict">
        <PassPill pass={passed}>{data.verdict}</PassPill>
      </FieldRow>
      {data.violations?.length > 0 && (
        <FieldRow label="Violations">
          <Stack>
            {data.violations.map((v, i) => (
              <Card key={i} tone="bg-[var(--danger-bg)] ring-[var(--danger-ring)]">
                <span className="text-[12px] leading-[1.55] text-[var(--danger)]">{typeof v === "string" ? v : v.detail}</span>
              </Card>
            ))}
          </Stack>
        </FieldRow>
      )}
      {data.policy && <FieldRow label="On violation"><span className="text-[12.5px]">{data.policy}</span></FieldRow>}
    </div>
  );
}

/* A verdict nobody can audit is an assertion, not a verification — so the
   individual checks are the body of this panel, not a footnote to it. */
function VerifierOutput({ data }: { data: VerifierOutputPayload }) {
  const checks = data.checks || [];
  const passed = data.verdict !== "fail";
  return (
    <div>
      <FieldRow label="Verdict">
        <PassPill pass={passed}>{data.verdict}</PassPill>
      </FieldRow>
      {checks.length > 0 && (
        <div className="py-2">
          <div className="text-[11.5px] text-[var(--text-tertiary)] mb-1.5">
            Checks · {checks.filter((c) => c.pass).length}/{checks.length} passed
          </div>
          <Stack>
            {checks.map((c, i) => (
              <Card key={i} tone={c.pass ? "" : "bg-[var(--danger-bg)] ring-[var(--danger-ring)]"}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-[12.5px] truncate ${c.pass ? "text-[var(--text-primary)]" : "text-[var(--danger)]"}`}>{c.name}</span>
                  <span className="ml-auto shrink-0"><PassPill pass={c.pass} /></span>
                </div>
                {c.detail && <div className="font-mono text-[11.5px] text-[var(--text-tertiary)] mt-1">{c.detail}</div>}
              </Card>
            ))}
          </Stack>
        </div>
      )}
      {data.note && <FieldRow label="Note"><TruncText text={data.note} className="text-[12.5px] leading-[1.6]" /></FieldRow>}
    </div>
  );
}

/* ----------------------------- human and policy ---------------------------- */

function ApprovalInput({ data }: { data: ApprovalInputPayload }) {
  return (
    <div>
      <FieldRow label="Request">
        <TruncText text={data.request} className="text-[12.5px] leading-[1.6]" />
      </FieldRow>
      {data.policy && <FieldRow label="Policy"><span className={chipCls}>{data.policy}</span></FieldRow>}
      {data.channel && <FieldRow label="Routed to"><span className={chipCls}>{data.channel}</span></FieldRow>}
    </div>
  );
}

/* Time-to-decision is recorded because an approval queue that nobody answers
   is the most common reason an "unattended" agent turns out not to be. */
function ApprovalOutput({ data }: { data: ApprovalOutputPayload }) {
  if (data.decision == null) {
    return (
      <div className="rounded-[8px] bg-[var(--warn-bg)] ring-1 ring-[var(--warn-ring)] px-3 py-2.5">
        <div className="text-[12px] font-medium text-[var(--warn-text)]">Still waiting on a human</div>
        <div className="text-[11.5px] leading-[1.5] text-[var(--warn-text)] opacity-80 mt-0.5">
          The run is paused here. Nothing was sent.
        </div>
      </div>
    );
  }
  const approved = data.decision === "approved";
  return (
    <div>
      <FieldRow label="Decision"><PassPill pass={approved}>{data.decision}</PassPill></FieldRow>
      {data.by && <FieldRow label="By"><span className="text-[12.5px]">{data.by}</span></FieldRow>}
      {data.waited_ms != null && <FieldRow label="Waited"><span className="font-mono text-[12.5px]">{fmtMs(data.waited_ms)}</span></FieldRow>}
      {data.auto && <FieldRow label="Auto-approved"><span className={chipCls}>by policy</span></FieldRow>}
    </div>
  );
}

/* Naming the exact call that was stopped is what makes a block actionable
   instead of mysterious. */
function GuardrailInput({ data }: { data: GuardrailInputPayload }) {
  return (
    <div>
      <FieldRow label="Attempted"><span className="font-mono text-[12.5px] break-all">{data.attempted}</span></FieldRow>
      {data.rule && <FieldRow label="Rule"><span className={chipCls}>{data.rule}</span></FieldRow>}
    </div>
  );
}

function GuardrailOutput({ data }: { data: GuardrailOutputPayload }) {
  return (
    <div className="rounded-[8px] bg-[var(--warn-bg)] ring-1 ring-[var(--warn-ring)] px-3 py-2.5">
      <div className="text-[12px] font-medium text-[var(--warn-text)]">
        {data.blocked ? "Blocked by policy" : "Allowed by policy"}
      </div>
      {data.detail && (
        <TruncText text={data.detail} className="block text-[11.5px] leading-[1.55] text-[var(--warn-text)] opacity-80 mt-0.5" />
      )}
    </div>
  );
}

function RetryOutput({ data }: { data: RetryOutputPayload }) {
  return (
    <div>
      <FieldRow label="Outcome"><span className="text-[12.5px]">{data.outcome}</span></FieldRow>
      {data.changed && (
        <FieldRow label="What changed">
          <TruncText text={data.changed} className="text-[12.5px] leading-[1.6]" />
        </FieldRow>
      )}
    </div>
  );
}

const TIERS = ["fast", "slow"] as const;

/* The two models Jev chose between, the one that took the message checked. */
function RouteInput({ data, span }: { data: RouteInputPayload; span?: Span }) {
  return (
    <div>
      {TIERS.map((tier) => (
        <FieldRow key={tier} label={tier === "fast" ? "Fast model" : "Slow model"}>
          <span className="inline-flex items-center gap-2">
            <span className="font-mono text-[12.5px]">{data.models?.[tier] ?? "—"}</span>
            {span?.tier === tier && (
              <span className="inline-flex items-center gap-1 text-[11.5px] text-[var(--info)]">
                <Check className="w-3 h-3" strokeWidth={2} />
                picked
              </span>
            )}
          </span>
        </FieldRow>
      ))}
    </div>
  );
}

/* Jev's pick: the model that took the message, how sure Jev was, and why the main model took
   one Jev didn't send it. */
function RouteOutput({ data }: { data: RouteOutputPayload }) {
  return (
    <div>
      <FieldRow label="Picked"><TierChip tier={data.tier} /></FieldRow>
      {data.confidence != null && (
        <FieldRow label="Confidence">
          <span className="inline-flex items-center gap-2">
            <MiniBar value={data.confidence} tone="info" width={64} />
            <span className="font-mono text-[12px] tabular-nums text-[var(--text-primary)]">{Math.round(data.confidence * 100)}%</span>
          </span>
        </FieldRow>
      )}
      {data.reason && (
        <FieldRow label="Why the main model took it">
          <span className="text-[12.5px] leading-[1.6]">{data.reason}</span>
        </FieldRow>
      )}
    </div>
  );
}

/* Compaction is the one step that makes context *smaller*, so it is drawn as a
   ratio — what survived out of what there was. */
function CompactionOutput({ data }: { data: CompactionOutputPayload }) {
  const before = data.after_tokens + data.dropped_tokens || 1;
  return (
    <div>
      <FieldRow label="Context">
        <span className="inline-flex items-center gap-2">
          <MiniBar value={data.after_tokens / before} tone="info" width={64} />
          <span className="font-mono text-[12px] tabular-nums text-[var(--text-primary)]">
            {fmtTokens(data.after_tokens)} kept
            <span className="text-[var(--text-quaternary)]"> · </span>
            <span className="text-[var(--warn-text)]">{fmtTokens(data.dropped_tokens)} dropped</span>
          </span>
        </span>
      </FieldRow>
      {data.summary && (
        <FieldRow label="What was dropped">
          <TruncText text={data.summary} className="text-[12.5px] leading-[1.6]" />
        </FieldRow>
      )}
    </div>
  );
}

function WebhookOutput({ data }: { data: WebhookOutputPayload }) {
  const ok = data.status >= 200 && data.status < 300;
  return (
    <div>
      <FieldRow label="Response"><PassPill pass={ok}>{String(data.status)}</PassPill></FieldRow>
      {data.response && (
        <FieldRow label="Body">
          <TruncText mono text={data.response} className="text-[12px] leading-[1.6]" />
        </FieldRow>
      )}
    </div>
  );
}

/* --------------------------------- registry -------------------------------- */

/* kind → { input, output } renderer. A kind with no entry falls through to the
   generic JSON view, which is always correct if never delightful. */
/* A payload renderer. Each one declares the payload shape its kind produces,
   but the registry is keyed by a runtime kind string — so the props are erased
   here and re-asserted by the renderer's own type. This and `SpanIo` are the
   two places the feature gives up type safety, and they are the same seam. */
// TODO(types): a payload union discriminated on TraceKind would let the
// registry keep each renderer's real props.
type PayloadView = ComponentType<{ data: any; span?: Span }>;

export const KIND_VIEWS: Partial<Record<TraceKind, { input?: PayloadView; output?: PayloadView }>> = {
  memory: { output: MemoryOutput },
  knowledge: { output: KnowledgeOutput },
  skill: { input: SkillInput },
  plan: { output: PlanOutput },
  thinking: { output: ThinkingOutput },
  search: { output: SearchOutput },
  code: { input: CodeInput, output: CodeOutput },
  edit: { output: EditOutput },
  subagent: { output: SubagentOutput },
  handoff: { input: HandoffInput, output: HandoffOutput },
  verifier: { output: VerifierOutput },
  approval: { input: ApprovalInput, output: ApprovalOutput },
  guardrail: { input: GuardrailInput, output: GuardrailOutput },
  retry: { output: RetryOutput },
  route: { input: RouteInput, output: RouteOutput },
  compaction: { output: CompactionOutput },
  webhook: { output: WebhookOutput },
};

export const kindView = (kind: TraceKind, which: "input" | "output"): PayloadView | null =>
  KIND_VIEWS[kind]?.[which] || null;

/* ----------------------------- effect ledger ------------------------------- */

const SEVERITY_TONE: Record<string, string> = {
  high: "bg-[var(--danger-bg)] ring-[var(--danger-ring)] text-[var(--danger)]",
  medium: "bg-[var(--warn-bg)] ring-[var(--warn-ring)] text-[var(--warn-text)]",
  low: "bg-[var(--surface-2)] ring-[var(--hairline)] text-[var(--text-secondary)]",
};

/* Everything the run changed outside itself. Reads are deliberately excluded —
   a ledger that lists a web search next to a sent email teaches people to skim
   it, and then it stops working as a ledger. */
/** `inCard`: rows only, for a card that already frames them (the step panel's `ListCard`). */
export function EffectLedger({ effects, onSelect, inCard = false }: {
  effects: EffectEntry[];
  onSelect?: (id: string) => void;
  inCard?: boolean;
}) {
  if (!effects.length) {
    return (
      <div className={inCard ? "px-4 py-4 text-center" : "rounded-[10px] border border-dashed border-[var(--hairline-strong)] px-3.5 py-5 text-center"}>
        <div className="text-[12.5px] font-medium text-[var(--text-primary)]">Nothing left this run</div>
        <div className="text-[11.5px] text-[var(--text-tertiary)] mt-0.5">It read and reasoned, but changed nothing outside the workspace.</div>
      </div>
    );
  }
  return (
    <div className={inCard ? "divide-y divide-[var(--hairline)]" : "rounded-[10px] ring-1 ring-[var(--hairline)] overflow-hidden divide-y divide-[var(--hairline)]"}>
      {effects.map((e, i) => (
        <button
          key={i}
          onClick={() => onSelect?.(e.span.id)}
          className={`press w-full text-left ${inCard ? "px-4" : "px-3"} py-2.5 bg-[var(--surface)] hover:bg-[var(--accent-hover)]`}
        >
          <div className="flex items-baseline gap-2 min-w-0">
            <span className={`shrink-0 inline-flex items-center h-[18px] px-1.5 rounded-full ring-1 font-mono text-[10px] font-medium ${SEVERITY_TONE[e.severity] || SEVERITY_TONE.low}`}>
              {e.status === "pending" ? "held" : e.status === "error" ? "failed" : e.severity}
            </span>
            {/* verb and target wrap together: the inspector is too narrow to truncate the target to a few letters */}
            <span className="min-w-0 break-words leading-[1.45]">
              <span className="text-[12.5px] font-medium text-[var(--text-primary)]">{e.verb}</span>{" "}
              <span className="text-[12px] text-[var(--text-tertiary)]">{e.target}</span>
            </span>
            <span className="ml-auto shrink-0 font-mono text-[11px] text-[var(--text-quaternary)]">+{fmtMs(e.at)}</span>
          </div>
          {e.detail && (
            <div className="text-[11.5px] leading-[1.5] text-[var(--text-tertiary)] mt-1 truncate">{e.detail}</div>
          )}
          {e.status === "pending" && (
            <div className="text-[11.5px] text-[var(--warn-text)] mt-1">Held for approval — nothing has been sent.</div>
          )}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------- spend panel ------------------------------- */

/* Where the money went, ranked. "Which step burns the money" is a question the
   analytics strip can only answer in aggregate. */
export function SpendList({ items, total, onSelect }: {
  items: BillableSpan[];
  total: number;
  onSelect?: (id: string) => void;
}) {
  if (!items.length) return <EmptyNote>No billable steps.</EmptyNote>;
  return (
    <div className="space-y-1">
      {items.map((s) => (
        <button
          key={s.id}
          onClick={() => onSelect?.(s.id)}
          className="press w-full flex items-center gap-2 px-2 py-1.5 rounded-[7px] hover:bg-[var(--accent-hover)] text-left"
        >
          <span className="font-mono text-[11.5px] text-[var(--text-primary)] truncate max-w-[110px]">{s.name}</span>
          <span className="text-[11.5px] text-[var(--text-tertiary)] truncate flex-1 min-w-0">{s.label}</span>
          <MiniBar value={total ? s.cost / total : 0} tone="info" width={40} />
          <span className="font-mono text-[11.5px] tabular-nums text-[var(--text-primary)] shrink-0">{money(s.cost)}</span>
        </button>
      ))}
    </div>
  );
}

export { ErrorNote };
